import { t, Trans } from '@lingui/macro';
import { Search, Loader2, Globe, Lock, Crown, Users, Calendar, CheckCircle2 } from 'lucide-react';
import React, { useEffect, useState } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { useAppUIContext } from "../../context/AppUIContext";
import { Workspace, canJoinAsMember, getInvitationPolicy, isMemberDomainAllowed } from "../../models/Workspace";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { Util } from "../../services/Utils/Util";
import { UtilApp } from "../../services/Utils/UtilApp";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { useToast } from "../../components/ui/use-toast";
import { Badge } from "../../components/ui/badge";
import { cn } from "../../lib/utils";
import { IconUserPlus } from '../components/FigmaIcons';

export const WorkspaceJoinModal: React.FC<{ show: boolean, setShow: (v: boolean) => void }> = ({ show, setShow }) => {
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const { workspaces } = useAppUIContext();
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [workspace, setWorkspace] = useState<Workspace>();
  const [resultText, setResultText] = useState("");
  const { toast } = useToast();
  const [allowedWorkspaces, setAllowedWorkspaces] = useState<Workspace[]>([]);

  useEffect(() => {
    if (show) {
      setWorkspaceId("");
      setWorkspace(undefined);
      setInProgress(false);
      setResultText("");
      setAllowedWorkspaces([]);
      Fire.ws.getAllowedWorkspaces(Util.getDomain(My.user.email)).then(list => setAllowedWorkspaces(list));
    }
  }, [show]);

  async function findWorkspace() {
    setInProgress(true);
    const ws = await Fire.ws.getWorkspace(workspaceId);
    setInProgress(false);
    setWorkspace(ws);
    setResultText(ws ? "" : t`No workspace found with this ID.`);
  }

  async function joinWorkspace(workspace: Workspace) {
    setInProgress(true);
    try {
      const res = await Fire.ws.joinWorkspace({ workspaceId: workspace.id, invitationKey: null });
      if (res === 'JOINED') {
        toast({
          title: t`Success`,
          description: t`You have joined the workspace ${workspace.name}`
        });
        setShow(false);
        history.replace(`/ws/${workspace.id}`);
      }
      else if (res === 'NOT_ALLOWED') {
        toast({
          variant: "destructive",
          title: t`Access Denied`,
          description: t`You are not allowed to join ${workspace.name}`
        });
      }
      else if (res === 'ALREADY_MEMBER') {
        toast({
          title: t`Info`,
          description: t`You are already a member of ${workspace.name}`
        });
      } else {
        toast({
          variant: "destructive",
          title: t`Error`,
          description: "Unknown error, could not join workspace."
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t`Error`,
        description: `Error: ${(error as Error).message}`
      });
    }
    setInProgress(false);
  }

  function hasJoined(workspace: Workspace) {
    return !!workspaces.find(w => w.id === workspace.id);
  }

  function WorkspaceCard({ workspace, compact }: { workspace: Workspace, compact?: boolean }) {
    const joined = hasJoined(workspace);
    const policy = getInvitationPolicy(workspace);
    // ID 검색 경로에는 초대 키가 없음 → 공개 또는 (도메인 제한 + 내 도메인 허용)일 때만 가입 가능
    const canJoin = canJoinAsMember(workspace, My.user.email, false);

    return (
      <Card className={cn(
        "w-full overflow-hidden transition-all duration-200",
        joined ? "border-green-200 bg-green-50/30" : "hover:shadow-md border-border"
      )}>
        <CardContent className={compact ? "p-4" : "p-5"}>
          <div className="flex gap-4">
            {/* Workspace Icon */}
            <div className="shrink-0">
              {workspace.iconUrl ? (
                <img src={workspace.iconUrl} alt="" className={cn("rounded-xl object-cover border border-border", compact ? "w-12 h-12" : "w-14 h-14")} />
              ) : (
                <div className={cn("rounded-xl bg-[rgba(255,127,0,0.1)] text-primary flex items-center justify-center font-bold border border-[rgba(255,127,0,0.1)]", compact ? "w-12 h-12 text-lg" : "w-14 h-14 text-xl")}>
                  {workspace.name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 space-y-2">
              {/* Name + Access Badge */}
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className={cn("font-bold text-foreground truncate", compact ? "text-sm" : "text-base")}>
                  {workspace.name}
                </h3>
                {policy === 'ALLOW_ANY_EMAIL_DOMAIN' ? (
                  <Badge variant="outline" className="text-meta-sm h-5 gap-1 text-green-700 border-green-300 bg-green-50 shrink-0">
                    <Globe className="w-3 h-3" />
                    <Trans>Public</Trans>
                  </Badge>
                ) : policy === 'ALLOW_LISTED_EMAIL_DOMAIN' ? (
                  <Badge variant="outline" className="text-meta-sm h-5 gap-1 text-amber-700 border-amber-300 bg-amber-50 shrink-0">
                    <Lock className="w-3 h-3" />
                    <Trans>Restricted</Trans>
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-meta-sm h-5 gap-1 text-muted-foreground border-border bg-muted/50 shrink-0">
                    <Lock className="w-3 h-3" />
                    <Trans>Private</Trans>
                  </Badge>
                )}
                {joined && (
                  <Badge variant="outline" className="text-meta-sm h-5 gap-1 text-green-700 border-green-300 bg-green-50 shrink-0">
                    <CheckCircle2 className="w-3 h-3" />
                    <Trans>Joined</Trans>
                  </Badge>
                )}
              </div>

              {/* Description */}
              {workspace.description?.trim() && (
                <p className={cn("text-muted-foreground leading-relaxed", compact ? "text-xs line-clamp-2" : "text-sm line-clamp-3")}>
                  {workspace.description}
                </p>
              )}

              {/* Meta Info Row */}
              <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                {/* Owner */}
                <div className="flex items-center gap-1.5">
                  <Avatar className="h-4 w-4">
                    <AvatarImage src={workspace.owner.photoUrl ?? Util.ANONYMOUS_PHOTO_URL} />
                    <AvatarFallback className="text-meta-sm">{workspace.owner.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <Crown className="w-3 h-3 text-amber-500" />
                  <span className="font-medium text-foreground truncate max-w-[100px]">{workspace.owner.name}</span>
                </div>

                <span className="text-muted-foreground">•</span>

                {/* Created */}
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-muted-foreground" />
                  <span>{UtilApp.dateStrToDay(workspace.created, My.user.lang)}</span>
                </div>

                {/* Restricted domains */}
                {policy === 'ALLOW_LISTED_EMAIL_DOMAIN' && workspace.emailDomains?.length > 0 && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      {workspace.emailDomains.map(domain => (
                        <span key={domain} className={cn(
                          "text-meta-sm px-1.5 py-0.5 rounded-full font-medium",
                          Util.getDomain(My.user.email)?.toLowerCase() === domain.toLowerCase()
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-card-foreground"
                        )}>
                          @{domain}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Action Button */}
            <div className="shrink-0 flex items-center">
              {joined ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-green-700 border-green-300 hover:bg-green-50"
                  onClick={() => { setShow(false); history.push(`/ws/${workspace.id}`); }}
                >
                  <Trans>Open</Trans>
                </Button>
              ) : canJoin ? (
                <Button
                  onClick={() => joinWorkspace(workspace)}
                  disabled={inProgress}
                  size="sm"
                  className="bg-primary hover:bg-primary/90 text-on-primary-text gap-1.5"
                >
                  <IconUserPlus className="w-3.5 h-3.5" />
                  <Trans>Join</Trans>
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground text-right max-w-[120px]">
                  {policy === 'ALLOW_LISTED_EMAIL_DOMAIN' && !isMemberDomainAllowed(workspace, My.user.email)
                    ? <Trans>Your email domain is not allowed</Trans>
                    : <Trans>Joining requires an invitation link</Trans>}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Dialog open={show} onOpenChange={setShow}>
      <DialogContent className="sm:max-w-md md:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle><Trans>Join a workspace</Trans></DialogTitle>
          <DialogDescription className="text-xs">
            <Trans>Enter a workspace ID to search, or browse workspaces available for your email domain.</Trans>
          </DialogDescription>
        </DialogHeader>

        {inProgress && (
          <div className="flex justify-center p-2 w-full">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        <div className="flex flex-col gap-4 py-2">
          {/* Search */}
          <div className="space-y-2">
            <label className="text-sm font-medium"><Trans>Workspace ID</Trans></label>
            <div className="flex w-full items-center space-x-2">
              <Input
                className="flex-1"
                value={workspaceId}
                onKeyDown={e => UtilApp.runOnEnter(e, findWorkspace)}
                onChange={e => setWorkspaceId(e.target.value ?? "")}
                placeholder={t`Enter a Workspace ID`}
                maxLength={24}
                disabled={inProgress}
              />
              <Button type="button" size="icon" disabled={!workspaceId || inProgress} onClick={findWorkspace} variant="secondary">
                <Search className="h-4 w-4" />
              </Button>
            </div>
            {resultText && (
              <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Search className="w-3.5 h-3.5" />
                {resultText}
              </p>
            )}
          </div>

          {/* Search Result */}
          {workspace && <WorkspaceCard workspace={workspace} />}

          {/* Allowed Workspaces */}
          {allowedWorkspaces.length > 0 && (
            <div className="mt-2 pt-4 border-t space-y-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                <h4 className="text-sm font-semibold text-foreground">
                  <Trans>Available for @{Util.getDomain(My.user.email)}</Trans>
                </h4>
              </div>
              <div className="space-y-2">
                {allowedWorkspaces.map(ws => <WorkspaceCard key={ws.id} workspace={ws} compact />)}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default WorkspaceJoinModal;